import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Calendar,
  Eye,
  ArrowRight
} from "lucide-react";
import {
  listGallyoSentimentArticle,
  listMarketPulseArticle
} from "../services/article";
import { useNavigate } from "react-router-dom";

const slugify = text => {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
};

// ## UPDATED HERO SECTION COMPONENT ##
function HeroSection({ article, onNext, onPrev, currentIndex, totalArticles }) {
  const navigate = useNavigate();

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  const handleNavigate = () => {
    const slug = slugify(article.article_title);
    navigate(
      `/gallionomics/details/${slugify(article.category.name)}/${slug}/${
        article.id
      }`
    );
  };

  const handlePrevClick = e => {
    e.stopPropagation(); // Prevent navigation when clicking the button
    onPrev();
  };

  const handleNextClick = e => {
    e.stopPropagation(); // Prevent navigation when clicking the button
    onNext();
  };

  return (
    <div
      className="relative w-full h-[450px] md:h-96 lg:h-[500px] mb-12 rounded-2xl overflow-hidden shadow-lg cursor-pointer group"
      onClick={handleNavigate}
    >
      <img
        src={article.cover_image_url}
        alt={article.article_title}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8 text-white">
        <div className="flex items-center gap-4 mb-4">
          <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-amber-100 text-amber-800 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            {article.category.name}
          </span>
          <div className="flex items-center gap-2 text-sm text-gray-200">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(article.created_at)}</span>
          </div>
        </div>

        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-fraunces mb-3 leading-tight">
          {article.article_title}
        </h1>

        {article.article_subtitle && (
          <p className="text-base lg:text-lg text-gray-200 leading-relaxed mb-4 max-w-3xl">
            {article.article_subtitle}
          </p>
        )}

        <div className="flex items-center gap-2 text-amber-200 font-medium group-hover:text-amber-100 transition-colors">
          <span>Read Article</span>
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </div>
      </div>

      {/* Navigation Controls */}
      <button
        onClick={handlePrevClick}
        className="absolute top-1/2 left-4 -translate-y-1/2 p-2 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors z-10"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={handleNextClick}
        className="absolute top-1/2 right-4 -translate-y-1/2 p-2 rounded-full bg-black/30 hover:bg-black/50 text-white transition-colors z-10"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Indicators */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {Array.from({ length: totalArticles }).map((_, index) => (
          <div
            key={index}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
              currentIndex === index ? "bg-white" : "bg-white/50"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function MarketCard({ article }) {
  const navigate = useNavigate();
  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const handleClick = () => {
    const slug = slugify(article.article_title);
    navigate(
      `/gallionomics/details/${slugify(article.category.name)}/${slug}/${
        article.id
      }`
    );
  };

  return (
    <div
      onClick={handleClick}
      className="w-[300px] bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex-shrink-0 cursor-pointer hover:shadow-md transition-shadow duration-300 group"
    >
      <div className="relative w-full h-48 overflow-hidden">
        <img
          src={article.cover_image_url}
          alt={article.article_title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      </div>

      <div className="p-4">
        <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 mb-2">
          {article.category.name}
        </div>

        <h2 className="cursor-pointer text-lg font-bold text-gray-900 mb-2 line-clamp-2 leading-tight font-afacad">
          {article.article_title}
        </h2>

        <div className="flex items-center justify-between text-xs text-gray-500 font-afacad">
          <span>{formatDate(article.created_at)}</span>
        </div>
      </div>
    </div>
  );
}

function SentimentCard({ article }) {
  const navigate = useNavigate();
  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const handleClick = () => {
    const slug = slugify(article.article_title);
    navigate(
      `/gallionomics/details/${slugify(article.category.name)}/${slug}/${
        article.id
      }`
    );
  };

  return (
    <div
      onClick={handleClick}
      className="w-[300px] bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex-shrink-0 cursor-pointer hover:shadow-md transition-shadow duration-300 group"
    >
      <div className="relative w-full h-48 overflow-hidden">
        <img
          src={article.cover_image_url}
          alt={article.article_title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      </div>

      <div className="p-4">
        <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 mb-2">
          {article.category.name}
        </div>

        <h2 className="cursor-pointer text-lg font-bold text-gray-900 mb-2 line-clamp-2 leading-tight font-afacad">
          {article.article_title}
        </h2>

        <div className="flex items-center justify-between text-xs text-gray-500 font-afacad">
          <span>{formatDate(article.created_at)}</span>
        </div>
      </div>
    </div>
  );
}

function ArticleCarousel({ articles, title }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCards, setVisibleCards] = useState(4);

  useEffect(() => {
    const updateVisibleCards = () => {
      if (window.innerWidth < 640) {
        setVisibleCards(1);
      } else if (window.innerWidth < 1024) {
        setVisibleCards(2);
      } else if (window.innerWidth < 1280) {
        setVisibleCards(3);
      } else {
        setVisibleCards(4);
      }
    };

    updateVisibleCards();
    window.addEventListener("resize", updateVisibleCards);
    return () => window.removeEventListener("resize", updateVisibleCards);
  }, []);

  const maxIndex = Math.max(0, articles.length - visibleCards);

  const nextSlide = () => {
    setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
  };

  const prevSlide = () => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  };

  return (
    <div className="mb-12">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-fraunces text-gray-900">
          {title}
        </h2>
        <div className="flex gap-2">
          <button
            onClick={prevSlide}
            disabled={currentIndex === 0}
            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={nextSlide}
            disabled={currentIndex === maxIndex}
            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="relative overflow-hidden">
        <div
          className="flex transition-transform duration-500 ease-out gap-4"
          style={{ transform: `translateX(-${currentIndex * (300 + 16)}px)` }}
        >
          {title === "Market Pulse"
            ? articles &&
              articles.map(article => (
                <MarketCard key={article.id} article={article} />
              ))
            : articles &&
              articles.map(article => (
                <SentimentCard key={article.id} article={article} />
              ))}
        </div>
      </div>
    </div>
  );
}

// ## UPDATED MAIN PAGE COMPONENT ##
function GallynomicsMainPage() {
  const [articles, setArticles] = useState([]);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);

  const [marketPulse, setMarketPulse] = useState(null);
  const [gallyoSentiment, setGallyoSentiment] = useState(null);

  useEffect(() => {
    async function fetchAllArticles() {
      try {
        const marketResponse = await listMarketPulseArticle({
          offset: 0,
          limit: 10
        });
        const marketPulses = marketResponse.map(rp => {
          return { ...rp, category: { name: "Market Pulse" } };
        });
        setMarketPulse(marketPulses);

        const sentimentResponse = await listGallyoSentimentArticle({
          offset: 0,
          limit: 10
        });
        const marketSentiment = sentimentResponse.map(rp => {
          return { ...rp, category: { name: "Gallyo Sentiment" } };
        });

        setGallyoSentiment(marketSentiment);

        setArticles([
          ...marketPulses.slice(0, 2),
          ...marketSentiment.slice(0, 2)
        ]);
      } catch (error) {
        console.error("Error while trying to fetch market pulses: ", error);
      }
    }

    fetchAllArticles();
  }, []);

  const handleNextHero = () => {
    setCurrentHeroIndex(prevIndex => (prevIndex + 1) % articles.length);
  };

  const handlePrevHero = () => {
    setCurrentHeroIndex(
      prevIndex => (prevIndex - 1 + articles.length) % articles.length
    );
  };

  useEffect(
    () => {
      if (!articles.length) return;

      const timer = setInterval(() => {
        handleNextHero();
      }, 3000);

      return () => clearInterval(timer);
    },
    [articles]
  );

  return (
    articles && (
      <div className="min-h-screen bg-gray-50 font-afacad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Hero Section */}
          {articles[currentHeroIndex] && (
            <HeroSection
              article={articles[currentHeroIndex]}
              onNext={handleNextHero}
              onPrev={handlePrevHero}
              currentIndex={currentHeroIndex}
              totalArticles={articles.length}
            />
          )}

          {/* Latest Articles Carousel */}
          {marketPulse && (
            <ArticleCarousel articles={marketPulse} title="Market Pulse" />
          )}

          {/* High Impact Articles Carousel */}
          {gallyoSentiment && (
            <ArticleCarousel
              articles={gallyoSentiment}
              title="Gallyo Sentiment"
            />
          )}
        </div>
      </div>
    )
  );
}

export default GallynomicsMainPage;
